import { Color } from '@persona-id/design-tokens';
import { camelCase, debounce } from 'lodash/fp';
import { useCallback, useEffect, useState } from 'react';
import { css } from 'styled-components';

import { TRANSLATIONS_DEBOUNCE_MILLISECONDS } from 'dashboard/apps/InquiryTemplateEditor/TranslationInputs/TranslationInputs';
import { useEscListener } from 'lib/hooks/useKeyListener';
import { KebabCaseToCamelCase } from 'lib/types';

export type UseContentEditableTargetNodeDataAttribute = `data-${string}`;
export type UseContentEditableDataset = DOMStringMap & {
  [key in KebabCaseToCamelCase<UseContentEditableTargetNodeDataAttribute>]: string;
} & {
  inlineEditable: string;
};

export interface UseContentEditableElement extends HTMLElement {
  dataset: UseContentEditableDataset;
}

// Note(Evan): Can either use as whole css export
// Or actually make this generic and extendible in call sites
export const baseContentEditableStyles = css`
  [data-inline-editable] {
    white-space: pre-wrap;
    display: inline-block;
    user-select: text;
    cursor: default;

    &:hover {
      box-shadow: inset 0 0 0px 1px ${Color.BorderInputActive};
    }

    &:focus,
    &[contenteditable='true'] {
      outline: none;
      box-shadow: inset 0px 0px 0px 1px ${Color.BorderInputActive};
    }
  }

  &:has([contenteditable='true']) {
    outline: none;
    box-shadow: none;
  }

  &:has([data-inline-editable]) {
    a {
      pointer-events: visible;
    }

    &:focus-within label {
      color: ${Color.PrimaryBlack};
    }
  }
`;

export const DEFAULT_USE_CONTENT_EDITABLE_DATA_TRANSLATIONS_ATTR = 'data-translations-key';

export const useContentEditable = ({
  wrapper,
  translationNodes,
  onUpdate,
  targetNodeDataAttribute = DEFAULT_USE_CONTENT_EDITABLE_DATA_TRANSLATIONS_ATTR,
}: {
  wrapper?: HTMLElement | null;
  translationNodes: Node[];
  onUpdate: (text: string, translationsKey: string) => void;
  targetNodeDataAttribute?: UseContentEditableTargetNodeDataAttribute;
}) => {
  const [currentEditable, setCurrentEditable] = useState<UseContentEditableElement | null>(null);
  const camelCaseTranslationsAttr = camelCase(targetNodeDataAttribute.replace(/(data)/, ''));

  const wrappedUpdate = useCallback(
    (ev: KeyboardEvent) => {
      if (currentEditable == null) return;
      if (ev.key === 'Enter') {
        ev.preventDefault();
        ev.stopPropagation();
        // add newline to textContent, and then specifically the ascii to the config
        currentEditable.textContent = `${currentEditable.textContent}\n`;
        onUpdate(
          `${currentEditable.textContent}\n`,
          currentEditable.dataset[camelCaseTranslationsAttr] as string,
        );
        return;
      }

      if (ev.key === 'Backspace') {
        ev.preventDefault();
        ev.stopPropagation();
      }

      onUpdate(
        currentEditable.textContent || '',
        currentEditable.dataset[camelCaseTranslationsAttr] as string,
      );
    },
    [onUpdate, currentEditable, camelCaseTranslationsAttr],
  );

  const debouncedUpdate = debounce(TRANSLATIONS_DEBOUNCE_MILLISECONDS, wrappedUpdate);

  const onmouseover = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (currentEditable === e.currentTarget) return;
    },
    [currentEditable],
  );

  const removeCurrentEditable = useCallback(() => {
    if (currentEditable == null) return;
    onUpdate(
      currentEditable.textContent || '',
      currentEditable.dataset[camelCaseTranslationsAttr] as string,
    );
    currentEditable.removeAttribute('contenteditable');
    currentEditable.removeAttribute('autofocus');
    setCurrentEditable(null);
  }, [currentEditable, onUpdate, camelCaseTranslationsAttr]);

  useEffect(() => {
    const onDoubleClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (currentEditable === e.currentTarget) return;

      const targetDataKeys = Object.keys((e.target as HTMLElement).dataset);

      if (targetDataKeys.includes('inlineEditable')) {
        removeCurrentEditable();
        setCurrentEditable(e.target as UseContentEditableElement);
      }
    };

    if (wrapper && translationNodes.length) {
      translationNodes.forEach((node: Node) => {
        const editableNode = node as UseContentEditableElement;
        editableNode.setAttribute('data-inline-editable', '');
        editableNode.addEventListener('mouseenter', onmouseover);
      });

      wrapper.addEventListener('dblclick', onDoubleClick, true);

      return () => {
        translationNodes.forEach((node: Node) => {
          const editableNode = node as UseContentEditableElement;
          editableNode.removeAttribute('data-inline-editable');
          editableNode.removeEventListener('mouseenter', onmouseover);
        });

        wrapper.removeEventListener('dblclick', onDoubleClick, true);
      };
    }
  }, [translationNodes, wrapper, onmouseover, currentEditable, removeCurrentEditable]);

  useEffect(() => {
    // refocus currenteditable DOM node to force explicit deselect
    const onblur = (e: FocusEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (currentEditable != null) {
        currentEditable.focus();
        debouncedUpdate.cancel();
      }
    };

    const debouncedBlur = debounce(TRANSLATIONS_DEBOUNCE_MILLISECONDS, onblur);

    if (currentEditable != null) {
      currentEditable.setAttribute('contenteditable', 'true');
      currentEditable.addEventListener('blur', debouncedBlur);
      currentEditable.addEventListener('keydown', debouncedUpdate);

      return () => {
        currentEditable.removeEventListener('blur', debouncedBlur);
        currentEditable.removeEventListener('keydown', debouncedUpdate);
      };
    }
  }, [currentEditable, debouncedUpdate]);

  // focus element + make correct text selection based on cursor position
  useEffect(() => {
    if (currentEditable == null) return;
    currentEditable.focus();

    const selection = window?.getSelection();
    const node = selection?.focusNode;

    if (selection && node) {
      const offset = selection.focusOffset;

      const range = document.createRange();
      range.selectNode(currentEditable.childNodes[0]);

      range.setStart(node, offset);
      selection?.modify('move', 'backwards', 'character');

      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [currentEditable]);

  useEscListener(removeCurrentEditable);

  // Outside click handler - if screen is clicked, remove contenteditable from current DOM node
  // and trigger onUpdate
  useEffect(() => {
    const onOutsideClick = (event: MouseEvent): void => {
      if (wrapper && currentEditable && !wrapper.contains(event.target as Node)) {
        removeCurrentEditable();
      }
    };

    if (currentEditable) {
      document.addEventListener('mousedown', onOutsideClick, true);

      return () => {
        document.removeEventListener('mousedown', onOutsideClick, true);
      };
    }
  }, [currentEditable, wrapper, onUpdate, removeCurrentEditable]);

  return { currentEditable, translationNodes };
};
